/**
 * ReferralPauseDialog — "Pause referral" (the approved Dialogs artboard).
 *
 * Same shape as pausing a file (CLAUDE.md §18.6): a reason, capped at the
 * notes' 200 characters because it is journaled to the referral's notepad as
 * a human note, plus a resume date that becomes next_action_on. Nothing is
 * written here — onConfirm({ reason, resumeOn }) hands the values to the
 * detail view, which calls referralsApi.pauseReferral.
 *
 * A small reusable component (not inline JSX) so the same dialog can serve
 * any future surface that pauses a referral.
 */

import React, { useEffect, useState } from 'react';
import { NOTE_BODY_MAX } from '../../api/referralNotesApi';
import { addBusinessDays } from '../../utils/businessDays';
import { DialogFrame, DialogHead, FieldLabel, Hint, dialogStyles as ds } from './ReferralDialogChrome';

/** 'YYYY-MM-DD' from the noon-UTC Date addBusinessDays returns. */
function toYmd(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export default function ReferralPauseDialog({ open, referral, busy = false, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  // Default resume date: two business days out, like a new referral's
  // follow-up — editable, and required.
  const [resumeOn, setResumeOn] = useState(() => toYmd(addBusinessDays(new Date(), 2)));
  const [error, setError] = useState(null);

  // Reset the form each time the dialog opens.
  useEffect(() => {
    if (open) {
      setReason('');
      setResumeOn(toYmd(addBusinessDays(new Date(), 2)));
      setError(null);
    }
  }, [open]);

  const len = reason.length;
  const overLimit = len > NOTE_BODY_MAX;
  const counterColour =
    overLimit ? 'var(--cp-danger)' : len > NOTE_BODY_MAX * 0.75 ? 'var(--cp-referral-orange)' : 'var(--cp-text-muted)';

  const submit = () => {
    const trimmed = reason.trim();
    if (!trimmed) return setError('A reason is required.');
    if (overLimit) return setError(`Keep the reason to ${NOTE_BODY_MAX} characters.`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(resumeOn)) return setError('Choose the date to resume on.');
    setError(null);
    onConfirm && onConfirm({ reason: trimmed, resumeOn });
  };

  return (
    <DialogFrame open={open} onClose={onCancel} busy={busy} width={420} label="Pause referral">
      <DialogHead referral={referral} title="Pause referral" subtitle="Same shape as pausing a file." />

      <div style={ds.field}>
        <FieldLabel htmlFor="ref-pause-reason">Reason</FieldLabel>
        <textarea
          id="ref-pause-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Awaiting funds from pension payout"
          rows={3}
          maxLength={NOTE_BODY_MAX + 20}
          style={ds.textarea}
          disabled={busy}
        />
        <div style={{ ...ds.counter, color: counterColour }}>
          {len}/{NOTE_BODY_MAX}
        </div>
      </div>

      <div style={ds.field}>
        <FieldLabel htmlFor="ref-pause-resume">Resume on</FieldLabel>
        <input
          id="ref-pause-resume"
          type="date"
          value={resumeOn}
          onChange={(e) => setResumeOn(e.target.value)}
          style={ds.input}
          disabled={busy}
        />
        <Hint>
          Becomes the next-action date. The card returns to In Progress when you resume it; the
          needs-attention strip nudges you on this date.
        </Hint>
      </div>

      {error && <div style={ds.error}>{error}</div>}

      <div style={ds.row}>
        <button type="button" style={ds.secondary} onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          style={{ ...ds.primary, background: 'var(--cp-warning)' }}
          onClick={submit}
          disabled={busy || !reason.trim() || overLimit}
        >
          {busy ? 'Pausing…' : 'Pause'}
        </button>
      </div>
    </DialogFrame>
  );
}
